// Central sport configuration.
//
// A single build-time switch drives every sport-specific difference between the
// Waterpolo and Handball editions of the app. CRA inlines `process.env.REACT_APP_*`
// at build time, so `REACT_APP_SPORT=handball yarn build` produces a handball
// bundle and the default (unset) produces the waterpolo bundle.
//
// Everything that differs per sport at the code level (colours, court geometry,
// formations, logos, brand copy) should read from here rather than branching on
// the env var directly, so the switch lives in exactly one place.

import wpLogoIcon from './assets/wp_logo_icon.png';
import wpLogoName from './assets/wp_logo_name_2_rows.png';
import hbLogoIcon from './assets/hb_logo_icon.png';
import hbLogoName from './assets/hb_logo_name_2_rows.png';

export type Sport = 'waterpolo' | 'handball';

export const SPORT: Sport =
  process.env.REACT_APP_SPORT === 'handball' ? 'handball' : 'waterpolo';

export const isHandball = SPORT === 'handball';

// Human-facing brand copy. Used for window/document titles, alt text, legal
// drawers, install prompts and the welcome/marketing surfaces.
export const appName = isHandball
  ? 'The Handball Playbook'
  : 'The Waterpolo Playbook';

export const appShortName = isHandball ? 'HB Playbook' : 'WP Playbook';

// Bare product name without the leading "The" — reads better inside sentences
// (legal drawers: "use of the Handball Playbook application").
export const productName = isHandball
  ? 'Handball Playbook'
  : 'Waterpolo Playbook';

// Lower-case noun used inside sentences ("designed for handball", "water polo
// coaches"). Note the waterpolo spelling is two words in prose.
export const sportNoun = isHandball ? 'handball' : 'water polo';

export const appLogoIconAlt = `${appName} icon`;
export const appLogoNameAlt = `${appName} logo`;

// Branded logo assets. Both editions ship the same two logo slots (a square
// icon and a two-row wordmark); the handball build swaps in its own artwork.
export const logoIcon = isHandball ? hbLogoIcon : wpLogoIcon;
export const logoName = isHandball ? hbLogoName : wpLogoName;

// Highest selectable player cap/jersey number in the numbering picker.
// Waterpolo caps run 1..14; handball jerseys go up to 99. Overridable per
// deployment with REACT_APP_MAX_CAP_NUMBER (e.g. a league with a different cap).
export const maxCapNumber: number = (() => {
  const fromEnv = parseInt(process.env.REACT_APP_MAX_CAP_NUMBER ?? '', 10);
  if (!Number.isNaN(fromEnv) && fromEnv > 0) return fromEnv;
  return isHandball ? 99 : 14;
})();
